import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { XMarkIcon, CurrencyDollarIcon, ChevronDownIcon, PhotoIcon, ClockIcon, AdjustmentsHorizontalIcon, PlusIcon, PlayIcon, PauseIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { IVideoGeneration, IUserModel, ModelType, VideoGenerationType, AspectRatioLabels, Resolution, ResolutionLabels, getLabelsByLocale, Locale } from '@packages/global';
import { useCoinStore } from '../../stores/coinStore';
import ImageUpload from '../common/ImageUpload';
import AudioUpload from '../common/AudioUpload';
import VideoTabs from '../common/VideoTabs';
import { ModelSelector } from '../common/form';
import MediaPreviewModal from '../common/MediaPreviewModal';
import AudioTrimmer from '../common/AudioTrimmer';

// 类型标签 i18n key 映射
const TypeShortLabelKeys: Record<VideoGenerationType, string> = {
  [VideoGenerationType.TEXT_TO_VIDEO]: 'video.type.text2video',
  [VideoGenerationType.IMAGE_TO_VIDEO]: 'video.type.image2video',
  [VideoGenerationType.KEYFRAME_TO_VIDEO]: 'video.type.keyframe2video',
  [VideoGenerationType.MULTI_IMAGE_TO_VIDEO]: 'video.type.multiImage2video'
};

interface VideoGenerationFormData {
  prompt: string;
  modelId: string;
  type: VideoGenerationType;
  duration: number;
  width: number;
  height: number;
  resolution: Resolution;
  aspectRatio: string;
  referenceImages: string[];
  referenceAudio?: string;
}

interface VideoGenerationFormProps {
  editingItem: IVideoGeneration | null;
  formData: VideoGenerationFormData;
  models: IUserModel[];
  modelsLoading: boolean;
  onSubmit: (e: React.FormEvent) => void;
  onFormDataChange: (data: Partial<VideoGenerationFormData>) => void;
  onAspectRatioChange: (ratio: string) => void;
  onGenerationTypeChange: (type: VideoGenerationType) => void;
  onReset: () => void;
}

export default function VideoGenerationForm({
  editingItem,
  formData,
  models,
  modelsLoading,
  onSubmit,
  onFormDataChange,
  onAspectRatioChange,
  onGenerationTypeChange,
  onReset
}: VideoGenerationFormProps) {
  const { t, i18n } = useTranslation(['generation', 'common']);
  const { account } = useCoinStore();
  const resolutionLabels = getLabelsByLocale(ResolutionLabels, i18n.language as Locale);
  const aspectRatioLabels = getLabelsByLocale(AspectRatioLabels, i18n.language as Locale);
  const selectedModel = models.find(m => m.id === formData.modelId);
  const baseModelPrice = selectedModel?.coinPrice ?? 0;
  const isAudioBilling = selectedModel?.modelType === ModelType.IMAGE_TO_VIDEO
    && Boolean(selectedModel.requiredInputs?.includes('audio'));

  // 下拉选择器状态
  const [showResolutionSelector, setShowResolutionSelector] = useState(false);
  const [showAspectRatioSelector, setShowAspectRatioSelector] = useState(false);
  const [showDurationDropdown, setShowDurationDropdown] = useState(false);
  const [showImageUpload, setShowImageUpload] = useState(false);
  const [showAudioUpload, setShowAudioUpload] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [showAudioTrimmer, setShowAudioTrimmer] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const resolutionSelectorRef = useRef<HTMLDivElement>(null);
  const aspectRatioSelectorRef = useRef<HTMLDivElement>(null);
  const durationSelectorRef = useRef<HTMLDivElement>(null);

  // 音频切换时重置播放状态
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = formData.referenceAudio || '';
    }
    setAudioPlaying(false);
  }, [formData.referenceAudio]);

  const toggleAudioPlay = () => {
    if (!formData.referenceAudio) return;
    if (!audioRef.current) {
      audioRef.current = new Audio(formData.referenceAudio);
      audioRef.current.onended = () => setAudioPlaying(false);
    }
    if (audioPlaying) {
      audioRef.current.pause();
      setAudioPlaying(false);
    } else {
      audioRef.current.play();
      setAudioPlaying(true);
    }
  };

  // 点击外部关闭选择器
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (resolutionSelectorRef.current && !resolutionSelectorRef.current.contains(event.target as Node)) {
        setShowResolutionSelector(false);
      }
      if (aspectRatioSelectorRef.current && !aspectRatioSelectorRef.current.contains(event.target as Node)) {
        setShowAspectRatioSelector(false);
      }
      if (durationSelectorRef.current && !durationSelectorRef.current.contains(event.target as Node)) {
        setShowDurationDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 计算实际价格
  const supportedDurations = selectedModel?.supportedDurations ?? [];
  const showDurationSelector = supportedDurations.length > 0;
  const duration = showDurationSelector ? formData.duration : 5;
  const resolutionMultiplier = formData.resolution === Resolution.FHD_1080P ? 2 : 1;
  // 音频计费 provider 不预估总价，只显示单价
  const modelPrice = isAudioBilling ? 0 : baseModelPrice * duration * resolutionMultiplier;

  const hasEnoughCoins = isAudioBilling || (account?.balance ?? 0) >= modelPrice;

  const getMaxImagesForType = (type: VideoGenerationType): number => {
    switch (type) {
      case VideoGenerationType.TEXT_TO_VIDEO: return 0;
      case VideoGenerationType.IMAGE_TO_VIDEO: return 1;
      case VideoGenerationType.KEYFRAME_TO_VIDEO: return 2;
      case VideoGenerationType.MULTI_IMAGE_TO_VIDEO: return 4;
      default: return 0;
    }
  };

  const handleImageUpload = (url: string) => {
    const maxImages = getMaxImagesForType(formData.type);
    if (formData.referenceImages.length < maxImages) {
      onFormDataChange({ referenceImages: [...formData.referenceImages, url] });
      setShowImageUpload(false);
    }
  };

  const removeReferenceImage = (index: number) => {
    onFormDataChange({ referenceImages: formData.referenceImages.filter((_, i) => i !== index) });
  };

  const handleGenerationTypeChange = (newType: VideoGenerationType) => {
    onFormDataChange({ referenceImages: [] });
    onGenerationTypeChange(newType);
  };

  // 是否需要参考图片
  const needsReferenceImages = formData.type !== VideoGenerationType.TEXT_TO_VIDEO;
  const maxImages = getMaxImagesForType(formData.type);

  // 模型是否支持音频输入
  const supportsAudioInput = selectedModel && (selectedModel.audioInputCount || 0) > 0;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // 手动验证 prompt
    if (!formData.prompt.trim()) {
      // 可以添加错误提示
      return;
    }

    onSubmit(e);
  };

  return (
    <div className="w-96 lg:w-[420px] bg-bg-form border-r border-border-subtle flex flex-col">
      <VideoTabs />

      <div className="flex-1 overflow-y-auto p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* 生成类型 Sub Tabs */}
          <div className="flex items-center justify-between">
            <div className="flex gap-4">
              {Object.entries(TypeShortLabelKeys).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => handleGenerationTypeChange(value as VideoGenerationType)}
                  className={`relative px-1 py-2 text-sm font-medium transition-colors ${formData.type === value
                    ? 'text-primary'
                    : 'text-gray-500 hover:text-primary'
                    }`}
                >
                  {t(label)}
                  {formData.type === value && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />
                  )}
                </button>
              ))}
            </div>
            {/* 重置按钮 */}
            {editingItem && (
              <button
                type="button"
                onClick={onReset}
                className="p-1.5 text-gray-400 hover:text-gray-200 hover:bg-bg-elevated rounded-lg transition-colors"
                title={t('video.form.resetForm')}
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* 模型选择 */}
          <div>
            <ModelSelector
              models={models}
              selectedModelId={formData.modelId}
              onChange={(modelId) => onFormDataChange({ modelId })}
              loading={modelsLoading}
              placeholder={t('video.form.selectModel')}
            />
          </div>

          {/* 输入容器 */}
          <div className="bg-bg-surface rounded-2xl p-4 border border-border-subtle transition-colors focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20">
            {/* 提示词输入 */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-500">{formData.prompt.length} / 5000</span>
              </div>
              <textarea
                value={formData.prompt}
                onChange={(e) => onFormDataChange({ prompt: e.target.value })}
                maxLength={5000}
                rows={4}
                className="w-full bg-transparent border-none focus:ring-0 text-gray-50 placeholder-gray-500 text-base resize-none py-0"
                placeholder={t('video.form.promptPlaceholder')}
              />
            </div>

            {/* 参考图片区域 - 需要图片时固定显示 */}
            {needsReferenceImages && (
              <div className="mt-3 pt-3 border-t border-border-subtle relative">
                <div className="flex gap-2 flex-wrap items-center">
                  {/* 已上传的图片 */}
                  {formData.referenceImages.map((url, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={url}
                        alt={t('video.form.referenceImageAlt', { index: index + 1 })}
                        className="w-12 h-12 object-cover rounded-lg border border-border-default cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => setPreviewImage(url)}
                      />
                      <button
                        type="button"
                        onClick={() => removeReferenceImage(index)}
                        className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors"
                      >
                        <XMarkIcon className="h-2.5 w-2.5" />
                      </button>
                      {formData.type === VideoGenerationType.KEYFRAME_TO_VIDEO && (
                        <div className="absolute bottom-0 left-0 right-0 px-1 py-0.5 bg-black/70 text-white text-[10px] text-center rounded-b-lg">
                          {index === 0 ? t('video.form.firstFrame') : t('video.form.lastFrame')}
                        </div>
                      )}
                    </div>
                  ))}

                  {/* 添加按钮 */}
                  {formData.referenceImages.length < maxImages && (
                    <button
                      type="button"
                      onClick={() => setShowImageUpload(true)}
                      className="w-12 h-12 bg-bg-elevated hover:bg-border-default rounded-lg flex items-center justify-center transition-colors border-2 border-dashed border-border-default"
                      title={t('video.form.addReferenceImage')}
                    >
                      <PlusIcon className="h-5 w-5 text-gray-400" />
                    </button>
                  )}

                  {/* 提示文字 */}
                  <span className="text-xs text-gray-500">
                    {t('video.form.referenceImageCount', { current: formData.referenceImages.length, max: maxImages })}
                  </span>
                </div>

                {/* 图片上传弹出面板 */}
                {showImageUpload && formData.referenceImages.length < maxImages && (
                  <div className="absolute bottom-full left-0 mb-2 bg-bg-surface border border-border-subtle rounded-lg shadow-lg z-50 p-3 min-w-[280px]">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs text-gray-400">{t('video.form.addReferenceImage')}</span>
                      <button
                        type="button"
                        onClick={() => setShowImageUpload(false)}
                        className="text-gray-500 hover:text-gray-300"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                    <ImageUpload showAssets={true} onUploadSuccess={handleImageUpload} />
                  </div>
                )}
              </div>
            )}

            {/* 参考音频区域 - 模型支持时固定显示 */}
            {supportsAudioInput && (
              <div className="mt-3 pt-3 border-t border-border-subtle relative">
                <div className="flex gap-2 flex-wrap items-center">
                  {formData.referenceAudio ? (
                    <div className="flex items-center gap-2 bg-bg-elevated rounded-lg px-3 py-2">
                      <button
                        type="button"
                        onClick={toggleAudioPlay}
                        className="flex-shrink-0 w-6 h-6 flex items-center justify-center text-blue-400 hover:text-blue-300 transition-colors"
                        title={audioPlaying ? 'Pause' : 'Play'}
                      >
                        {audioPlaying
                          ? <PauseIcon className="w-4 h-4" />
                          : <PlayIcon className="w-4 h-4" />
                        }
                      </button>
                      <span className="text-xs text-gray-300">{t('video.form.referenceAudio')}</span>
                      <button
                        type="button"
                        onClick={() => { audioRef.current?.pause(); setAudioPlaying(false); setShowAudioTrimmer(true); }}
                        className="p-0.5 text-gray-400 hover:text-yellow-400 transition-colors"
                        title={t('video.form.editAudio')}
                      >
                        <PencilSquareIcon className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => onFormDataChange({ referenceAudio: '' })}
                        className="p-0.5 text-gray-400 hover:text-red-400 transition-colors"
                      >
                        <XMarkIcon className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => setShowAudioUpload(true)}
                        className="w-12 h-12 bg-bg-elevated hover:bg-border-default rounded-lg flex items-center justify-center transition-colors border-2 border-dashed border-border-default"
                        title={t('video.form.addReferenceAudio')}
                      >
                        <PlusIcon className="h-5 w-5 text-gray-400" />
                      </button>
                      <span className="text-xs text-gray-500">{t('video.form.referenceAudioOptional')}</span>
                    </>
                  )}
                </div>

                {/* 音频上传弹出面板 */}
                {showAudioUpload && !formData.referenceAudio && (
                  <div className="absolute bottom-full left-0 mb-2 bg-bg-surface border border-border-subtle rounded-lg shadow-lg z-50 p-3 min-w-[280px]">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs text-gray-400">{t('video.form.addReferenceAudio')}</span>
                      <button
                        type="button"
                        onClick={() => setShowAudioUpload(false)}
                        className="text-gray-500 hover:text-gray-300"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                    <AudioUpload
                      value=""
                      onUploadSuccess={(url) => {
                        onFormDataChange({ referenceAudio: url });
                        setShowAudioUpload(false);
                      }}
                      placeholder={t('video.form.uploadReferenceAudio')}
                    />
                  </div>
                )}
              </div>
            )}

            {/* 音频截取编辑器（模态框，通过 portal 渲染到 body） */}
            {showAudioTrimmer && formData.referenceAudio && (
              <AudioTrimmer
                audioUrl={formData.referenceAudio}
                onConfirm={(url) => {
                  onFormDataChange({ referenceAudio: url });
                  setShowAudioTrimmer(false);
                }}
                onCancel={() => setShowAudioTrimmer(false)}
              />
            )}

            {/* 底部工具栏 */}
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border-subtle flex-wrap">
              {/* 时长选择 - 仅当 supportedDurations 非空时显示 */}
              {showDurationSelector && (
                <div className="relative" ref={durationSelectorRef}>
                  <button
                    type="button"
                    onClick={() => setShowDurationDropdown(!showDurationDropdown)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-bg-elevated hover:bg-border-default text-gray-200 text-[13px] rounded-lg border border-border-default transition-colors"
                  >
                    <ClockIcon className="w-3.5 h-3.5 text-green-400" />
                    <span>{t('video.form.durationSeconds', { duration: formData.duration })}</span>
                    <ChevronDownIcon className="w-3 h-3 text-gray-400" />
                  </button>
                  {showDurationDropdown && (
                    <div className="absolute bottom-full left-0 mb-2 bg-bg-surface border border-border-subtle rounded-lg shadow-lg z-50 py-1 min-w-[100px]">
                      {supportedDurations.map(dur => (
                        <button
                          key={dur}
                          type="button"
                          onClick={() => {
                            onFormDataChange({ duration: dur });
                            setShowDurationDropdown(false);
                          }}
                          className={`w-full px-3 py-2 text-left text-sm hover:bg-bg-elevated transition-colors ${formData.duration === dur ? 'text-primary bg-bg-elevated' : 'text-gray-200'
                            }`}
                        >
                          {t('video.form.durationSeconds', { duration: dur })}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* 分辨率选择 */}
              <div className="relative" ref={resolutionSelectorRef}>
                <button
                  type="button"
                  onClick={() => setShowResolutionSelector(!showResolutionSelector)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-bg-elevated hover:bg-border-default text-gray-200 text-[13px] rounded-lg border border-border-default transition-colors"
                >
                  <PhotoIcon className="w-3.5 h-3.5 text-purple-400" />
                  <span>{resolutionLabels[formData.resolution]}</span>
                  <ChevronDownIcon className="w-3 h-3 text-gray-400" />
                </button>
                {showResolutionSelector && (
                  <div className="absolute bottom-full left-0 mb-2 bg-bg-surface border border-border-subtle rounded-lg shadow-lg z-50 py-1 min-w-[140px]">
                    {Object.entries(resolutionLabels).map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => {
                          onFormDataChange({ resolution: value as Resolution });
                          setShowResolutionSelector(false);
                        }}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-bg-elevated transition-colors ${formData.resolution === value ? 'text-primary bg-bg-elevated' : 'text-gray-200'
                          }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* 宽高比选择 */}
              <div className="relative" ref={aspectRatioSelectorRef}>
                <button
                  type="button"
                  onClick={() => setShowAspectRatioSelector(!showAspectRatioSelector)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-bg-elevated hover:bg-border-default text-gray-200 text-[13px] rounded-lg border border-border-default transition-colors"
                >
                  <AdjustmentsHorizontalIcon className="w-3.5 h-3.5 text-blue-400" />
                  <span>{formData.aspectRatio}</span>
                  <ChevronDownIcon className="w-3 h-3 text-gray-400" />
                </button>
                {showAspectRatioSelector && (
                  <div className="absolute bottom-full left-0 mb-2 bg-bg-surface border border-border-subtle rounded-lg shadow-lg z-50 py-1 min-w-[160px]">
                    {Object.entries(aspectRatioLabels).map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => {
                          onAspectRatioChange(value);
                          setShowAspectRatioSelector(false);
                        }}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-bg-elevated transition-colors ${formData.aspectRatio === value ? 'text-primary bg-bg-elevated' : 'text-gray-200'
                          }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 生成按钮和剧点 */}
          <div className="pt-4 space-y-2">
            <button
              type="submit"
              disabled={modelsLoading || !isFormValid() || !hasEnoughCoins}
              className="w-full bg-gradient-to-br from-primary to-primary-light hover:shadow-[0_0_20px_rgba(255,92,0,0.3)] disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              {modelsLoading ? t('common:loading') : t('video.form.generateVideo')}
            </button>
            {!modelsLoading && baseModelPrice > 0 && (
              <div className="flex items-center justify-between text-xs px-1">
                <div className="flex items-center gap-1 text-gray-500">
                  <CurrencyDollarIcon className="w-3.5 h-3.5" />
                  {isAudioBilling ? (
                    <span>{t('video.form.unitPricePerSecond', { price: baseModelPrice })}</span>
                  ) : (
                    <span>{t('video.form.cost')} <span className="font-mono text-primary">{modelPrice}</span></span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-gray-500">{t('video.form.balance')}</span>
                  <span className={`font-mono ${hasEnoughCoins ? 'text-gray-300' : 'text-error'}`}>
                    {account?.balance ?? 0}
                  </span>
                </div>
              </div>
            )}
          </div>
        </form>
      </div>

      {/* 图片预览弹窗 */}
      <MediaPreviewModal
        isOpen={!!previewImage}
        onClose={() => setPreviewImage(null)}
        type="image"
        src={previewImage || ''}
        alt={t('video.form.referenceImagePreview')}
      />
    </div>
  );

  function isFormValid(): boolean {
    if (!formData.modelId || !formData.prompt.trim()) return false;

    // 验证模型的 requiredInputs
    if (selectedModel?.requiredInputs?.length) {
      for (const input of selectedModel.requiredInputs) {
        if (input === 'image' && formData.referenceImages.length === 0) return false;
        if (input === 'audio' && !formData.referenceAudio) return false;
      }
    }

    switch (formData.type) {
      case VideoGenerationType.TEXT_TO_VIDEO: return true;
      case VideoGenerationType.IMAGE_TO_VIDEO: return formData.referenceImages.length === 1;
      case VideoGenerationType.KEYFRAME_TO_VIDEO: return formData.referenceImages.length === 2;
      case VideoGenerationType.MULTI_IMAGE_TO_VIDEO: return formData.referenceImages.length >= 2 && formData.referenceImages.length <= 4;
      default: return false;
    }
  }
}
